import type { Project, ExportOptions, Chapter } from '../types';

interface EPUBChapter {
  title: string;
  content: string;
  filename: string;
  sectionType: string;
}

// Generate CSS styles based on options
function generateStyles(options: ExportOptions): string {
  const fontFamily = options.fontFamily === 'serif'
    ? 'Georgia, "Times New Roman", serif'
    : 'Helvetica, Arial, sans-serif';

  const fontSize = options.fontSize === 'small' ? '0.9em' : options.fontSize === 'large' ? '1.1em' : '1em';

  const lineHeight = options.lineSpacing === 'single' ? '1.4' : options.lineSpacing === 'double' ? '2.0' : '1.6';

  return `
    @page {
      margin: 1em;
    }
    body {
      font-family: ${fontFamily};
      font-size: ${fontSize};
      line-height: ${lineHeight};
      margin: 1em;
      text-align: justify;
      hyphens: auto;
      -webkit-hyphens: auto;
      orphans: 2;
      widows: 2;
    }
    h1 {
      font-size: 1.8em;
      text-align: center;
      margin: 2em 0 1em 0;
      page-break-after: avoid;
      page-break-before: always;
    }
    h2 {
      font-size: 1.4em;
      margin: 1.5em 0 0.5em 0;
      page-break-after: avoid;
    }
    h3 {
      font-size: 1.2em;
      margin: 1.2em 0 0.4em 0;
    }
    p {
      text-indent: 1.5em;
      margin: 0.3em 0;
    }
    p:first-of-type,
    h1 + p,
    h2 + p,
    h3 + p {
      text-indent: 0;
    }
    .chapter-number {
      font-size: 0.9em;
      text-transform: uppercase;
      letter-spacing: 0.2em;
      text-align: center;
      margin-bottom: 0.5em;
    }
    .title-page {
      text-align: center;
      padding-top: 30%;
    }
    .title-page h1 {
      font-size: 2.5em;
      margin: 0;
      page-break-before: avoid;
    }
    .title-page .subtitle {
      font-size: 1.3em;
      font-style: italic;
      margin-top: 0.5em;
    }
    .title-page .author {
      font-size: 1.5em;
      margin-top: 2em;
    }
    .copyright-page {
      font-size: 0.85em;
      text-align: center;
      padding-top: 60%;
    }
    .copyright-page p {
      text-indent: 0;
      margin: 0.5em 0;
    }
    .dedication {
      font-style: italic;
      text-align: center;
      padding-top: 30%;
    }
    .epigraph {
      font-style: italic;
      margin: 2em 3em;
      text-align: right;
    }
    .epigraph-source {
      margin-top: 0.5em;
    }
    blockquote {
      margin: 1em 2em;
      font-style: italic;
    }
    em { font-style: italic; }
    strong { font-weight: bold; }
    .scene-break {
      text-align: center;
      margin: 1.5em 0;
    }
    .scene-break::before {
      content: "* * *";
      letter-spacing: 0.5em;
    }
  `;
}

// Generate valid XHTML content
function generateXHTML(title: string, body: string, styles: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops">
<head>
  <meta charset="UTF-8"/>
  <title>${escapeXML(title)}</title>
  <link rel="stylesheet" type="text/css" href="styles.css"/>
</head>
<body>
${body}
</body>
</html>`;
}

function escapeXML(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function htmlToXHTML(html: string): string {
  if (!html || !html.trim()) {
    return '<p></p>';
  }

  let xhtml = html
    // Self-closing tags
    .replace(/<br\s*\/?>/gi, '<br/>')
    .replace(/<hr\s*\/?>/gi, '<hr/>')
    .replace(/<img([^>]*?)>/gi, '<img$1/>')
    // Remove any script/style tags
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    // Scene breaks
    .replace(/\* \* \*/g, '<p class="scene-break"></p>')
    .replace(/---/g, '<p class="scene-break"></p>');

  // If no HTML structure, wrap in paragraphs
  if (!xhtml.includes('<p>') && !xhtml.includes('<h')) {
    xhtml = xhtml
      .split(/\n\n+/)
      .filter((p) => p.trim())
      .map((p) => `<p>${escapeXML(p.trim().replace(/\n/g, '<br/>'))}</p>`)
      .join('\n');
  }

  return xhtml;
}

// Generate container.xml
function generateContainer(): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/>
  </rootfiles>
</container>`;
}

// Generate content.opf (package document)
function generateOPF(
  project: Project,
  chapters: EPUBChapter[],
  options: ExportOptions,
  hasCover: boolean
): string {
  const uuid = crypto.randomUUID();
  const now = new Date().toISOString();

  const manifestItems: string[] = [
    '    <item id="styles" href="styles.css" media-type="text/css"/>',
  ];

  if (hasCover) {
    manifestItems.push('    <item id="cover-image" href="cover.jpg" media-type="image/jpeg" properties="cover-image"/>');
  }

  if (options.includeTitle) {
    manifestItems.push('    <item id="title-page" href="title.xhtml" media-type="application/xhtml+xml"/>');
  }

  if (options.includeCopyright) {
    manifestItems.push('    <item id="copyright" href="copyright.xhtml" media-type="application/xhtml+xml"/>');
  }

  if (options.includeToc) {
    manifestItems.push('    <item id="toc" href="toc.xhtml" media-type="application/xhtml+xml" properties="nav"/>');
  }

  chapters.forEach((ch, i) => {
    manifestItems.push(`    <item id="chapter${i}" href="${ch.filename}" media-type="application/xhtml+xml"/>`);
  });

  const spineItems: string[] = [];

  if (options.includeTitle) {
    spineItems.push('    <itemref idref="title-page"/>');
  }
  if (options.includeCopyright) {
    spineItems.push('    <itemref idref="copyright"/>');
  }
  if (options.includeToc) {
    spineItems.push('    <itemref idref="toc"/>');
  }

  chapters.forEach((_, i) => {
    spineItems.push(`    <itemref idref="chapter${i}"/>`);
  });

  const keywords = project.metadata.keywords?.length
    ? `\n    <dc:subject>${project.metadata.keywords.join(', ')}</dc:subject>`
    : '';

  return `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" version="3.0" unique-identifier="uid">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/">
    <dc:identifier id="uid">urn:uuid:${uuid}</dc:identifier>
    <dc:title>${escapeXML(project.metadata.title || 'Untitled')}</dc:title>
    <dc:creator>${escapeXML(project.metadata.author || 'Unknown')}</dc:creator>
    <dc:language>${project.metadata.language || 'en'}</dc:language>
    <dc:description>${escapeXML(project.metadata.description || '')}</dc:description>${keywords}
    ${project.metadata.publisher ? `<dc:publisher>${escapeXML(project.metadata.publisher)}</dc:publisher>` : ''}
    ${project.metadata.isbn ? `<dc:identifier id="isbn">urn:isbn:${project.metadata.isbn.replace(/-/g, '')}</dc:identifier>` : ''}
    <meta property="dcterms:modified">${now.substring(0, 19)}Z</meta>
  </metadata>
  <manifest>
${manifestItems.join('\n')}
  </manifest>
  <spine>
${spineItems.join('\n')}
  </spine>
</package>`;
}

// Generate title page
function generateTitlePage(project: Project, styles: string): string {
  const body = `
<div class="title-page">
  <h1>${escapeXML(project.metadata.title || 'Untitled')}</h1>
  ${project.metadata.subtitle ? `<p class="subtitle">${escapeXML(project.metadata.subtitle)}</p>` : ''}
  ${project.metadata.author ? `<p class="author">${escapeXML(project.metadata.author)}</p>` : ''}
</div>`;

  return generateXHTML(project.metadata.title || 'Title', body, styles);
}

// Generate copyright page
function generateCopyrightPage(project: Project, styles: string): string {
  const year = project.metadata.publishDate
    ? new Date(project.metadata.publishDate).getFullYear()
    : new Date().getFullYear();

  const copyrightNotice = project.metadata.copyright ||
    `Copyright © ${year} ${project.metadata.author || 'Author'}.\nAll rights reserved.`;

  const body = `
<div class="copyright-page">
  <p>${escapeXML(project.metadata.title || 'Untitled')}</p>
  ${project.metadata.subtitle ? `<p>${escapeXML(project.metadata.subtitle)}</p>` : ''}
  <p><br/></p>
  ${copyrightNotice.split('\n').map(line => `<p>${escapeXML(line)}</p>`).join('\n')}
  ${project.metadata.isbn ? `<p><br/></p><p>ISBN: ${project.metadata.isbn}</p>` : ''}
  ${project.metadata.publisher ? `<p>${escapeXML(project.metadata.publisher)}</p>` : ''}
</div>`;

  return generateXHTML('Copyright', body, styles);
}

// Generate table of contents (NAV document)
function generateTOC(project: Project, chapters: EPUBChapter[], styles: string): string {
  // Only include main content in TOC
  const tocChapters = chapters.filter(ch =>
    ['chapter', 'prologue', 'epilogue', 'foreword', 'preface', 'afterword', 'acknowledgments', 'about-author'].includes(ch.sectionType)
  );

  const navItems = tocChapters
    .map((ch) => `      <li><a href="${ch.filename}">${escapeXML(ch.title)}</a></li>`)
    .join('\n');

  const body = `
<nav epub:type="toc" id="toc">
  <h1>Contents</h1>
  <ol>
${navItems}
  </ol>
</nav>`;

  return generateXHTML('Table of Contents', body, styles);
}

function getSectionLabel(chapter: Chapter): string {
  const labels: Record<string, string> = {
    'title-page': 'Title Page',
    'copyright': 'Copyright',
    'dedication': 'Dedication',
    'epigraph': 'Epigraph',
    'toc': 'Table of Contents',
    'foreword': 'Foreword',
    'preface': 'Preface',
    'prologue': 'Prologue',
    'chapter': 'Chapter',
    'epilogue': 'Epilogue',
    'afterword': 'Afterword',
    'acknowledgments': 'Acknowledgments',
    'about-author': 'About the Author',
    'also-by': 'Also By',
  };
  return labels[chapter.sectionType] || 'Chapter';
}

// Generate chapter XHTML
function generateChapterXHTML(chapter: Chapter, chapterNumber: number, styles: string): string {
  const content = htmlToXHTML(chapter.content || '');

  let body = '';

  // Special formatting for different section types
  if (chapter.sectionType === 'dedication') {
    body = `<div class="dedication">${content}</div>`;
  } else if (chapter.sectionType === 'epigraph') {
    body = `<div class="epigraph">${content}</div>`;
  } else {
    // Standard chapter with number and title
    const showChapterNumber = chapter.sectionType === 'chapter';

    body = `
${showChapterNumber ? `<p class="chapter-number">${getSectionLabel(chapter)} ${chapterNumber}</p>` : ''}
<h1>${escapeXML(chapter.title || getSectionLabel(chapter))}</h1>
${content}`;
  }

  return generateXHTML(chapter.title || 'Chapter', body, styles);
}

// Create ZIP blob using JSZip
async function createZipBlob(files: Map<string, string | Uint8Array>): Promise<Blob> {
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();

  // Mimetype must be first and uncompressed (EPUB spec)
  zip.file('mimetype', 'application/epub+zip', { compression: 'STORE' });

  for (const [path, content] of files) {
    if (path === 'mimetype') continue;
    zip.file(path, content);
  }

  return zip.generateAsync({
    type: 'blob',
    mimeType: 'application/epub+zip',
    compression: 'DEFLATE',
    compressionOptions: { level: 9 },
  });
}

// Convert base64 cover to Uint8Array
function base64ToUint8Array(base64: string): Uint8Array {
  // Remove data URL prefix if present
  const base64Data = base64.replace(/^data:image\/\w+;base64,/, '');
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

export async function exportToEPUB(
  project: Project,
  options: ExportOptions
): Promise<Blob> {
  const files = new Map<string, string | Uint8Array>();
  const styles = generateStyles(options);

  // Prepare chapters
  let chapterNumber = 0;
  const chapters: EPUBChapter[] = project.chapters.map((chapter, index) => {
    if (chapter.sectionType === 'chapter') {
      chapterNumber++;
    }
    return {
      title: chapter.title || `Chapter ${index + 1}`,
      content: htmlToXHTML(chapter.content || ''),
      filename: `chapter${index + 1}.xhtml`,
      sectionType: chapter.sectionType,
    };
  });

  // Check if we have a cover
  const hasCover = !!project.metadata.coverImage;

  // Add required EPUB files
  files.set('mimetype', 'application/epub+zip');
  files.set('META-INF/container.xml', generateContainer());
  files.set('OEBPS/content.opf', generateOPF(project, chapters, options, hasCover));
  files.set('OEBPS/styles.css', styles);

  // Add cover image if present
  if (hasCover && project.metadata.coverImage) {
    files.set('OEBPS/cover.jpg', base64ToUint8Array(project.metadata.coverImage));
  }

  // Add title page if requested
  if (options.includeTitle) {
    files.set('OEBPS/title.xhtml', generateTitlePage(project, styles));
  }

  // Add copyright page if requested
  if (options.includeCopyright) {
    files.set('OEBPS/copyright.xhtml', generateCopyrightPage(project, styles));
  }

  // Add TOC if requested
  if (options.includeToc) {
    files.set('OEBPS/toc.xhtml', generateTOC(project, chapters, styles));
  }

  // Add chapter files
  chapterNumber = 0;
  project.chapters.forEach((chapter, index) => {
    if (chapter.sectionType === 'chapter') {
      chapterNumber++;
    }
    files.set(
      `OEBPS/${chapters[index].filename}`,
      generateChapterXHTML(chapter, chapterNumber, styles)
    );
  });

  return createZipBlob(files);
}
